import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError, openAuthenticatedPdf } from "@/lib/apiClient";
import { usePortalAuth } from "@/lib/auth";
import {
  WartungPlanViewer,
  type PortalWartungDetail,
} from "@/pages/WartungPlanViewer";

type DocTyp =
  | "angebot"
  | "auftragsbestaetigung"
  | "lieferschein"
  | "rechnung"
  | "all";

type PortalDokument = {
  id: number;
  dokument_nummer?: string | null;
  dokument_typ?: string | null;
  status?: string | null;
  belegdatum?: string | null;
  betreff?: string | null;
  kunde_name?: string | null;
  summe_brutto?: number | null;
  waehrung?: string | null;
  auftrag_nummer?: string | null;
  auftrag_beschreibung?: string | null;
};

type PortalTimesheet = {
  id: number;
  timesheet_number?: number | null;
  display_number?: string | null;
  status?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  kunde_name?: string | null;
  auftrag_nummer?: string | null;
  auftrag_beschreibung?: string | null;
  has_pdf?: boolean;
  has_signed_pdf?: boolean;
};

type PortalWartungProject = {
  project_id: number;
  auftragsnummer?: string | null;
  beschreibung?: string | null;
  ort_name?: string | null;
  kunde_name?: string | null;
  sub_location_count?: number;
  plan_count?: number;
  ergebnis_count?: number;
};

const DOC_TYPE_LABELS: Record<string, string> = {
  angebot: "Angebot",
  auftragsbestaetigung: "Auftragsbestätigung",
  lieferschein: "Lieferschein",
  rechnung: "Rechnung",
};

const FILTERS: { id: DocTyp; label: string }[] = [
  { id: "all", label: "Alle" },
  { id: "angebot", label: "Angebote" },
  { id: "auftragsbestaetigung", label: "Auftragsbestätigungen" },
  { id: "lieferschein", label: "Lieferscheine" },
  { id: "rechnung", label: "Rechnungen" },
];

function formatDate(value?: string | null): string {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("de-DE");
}

function formatMoney(amount?: number | null, currency?: string | null): string {
  if (amount == null || Number.isNaN(amount)) return "–";
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || "EUR"}`;
  }
}

function statusLabel(status?: string | null): string {
  if (!status) return "–";
  const map: Record<string, string> = {
    erstellt: "Erstellt",
    versendet: "Versendet",
    angenommen: "Angenommen",
    abgelehnt: "Abgelehnt",
    geliefert: "Geliefert",
    abgerechnet: "Abgerechnet",
    signed: "Unterschrieben",
    ready: "Bereit",
    invoice_draft: "Rechnung in Arbeit",
    invoice_sent: "Abgerechnet",
  };
  return map[status] || status;
}

export function HomePage() {
  const { user, logout, impersonate, clearImpersonation } = usePortalAuth();
  const isStaff = user?.portal_role === "staff";
  const hasCustomerView = Boolean(user?.effective_email);
  const name = user?.displayName || user?.email || "Gast";

  const [impersonateEmail, setImpersonateEmail] = useState("");
  const [impersonateBusy, setImpersonateBusy] = useState(false);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);

  const [docFilter, setDocFilter] = useState<DocTyp>("all");
  const [docs, setDocs] = useState<PortalDokument[]>([]);
  const [timesheets, setTimesheets] = useState<PortalTimesheet[]>([]);
  const [wartungen, setWartungen] = useState<PortalWartungProject[]>([]);
  const [viewerProjectId, setViewerProjectId] = useState<number | null>(null);
  const [viewerDetail, setViewerDetail] = useState<PortalWartungDetail | null>(null);
  const [wartungDetailLoading, setWartungDetailLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasCustomerView) {
      setDocs([]);
      setTimesheets([]);
      setWartungen([]);
      setViewerProjectId(null);
      setViewerDetail(null);
      setDataError(null);
      return;
    }

    let cancelled = false;
    async function load() {
      setLoadingData(true);
      setDataError(null);
      try {
        const [docRes, tsRes, wartRes] = await Promise.all([
          api.get<{ items: PortalDokument[] }>("/portal/dokumente"),
          api.get<{ items: PortalTimesheet[] }>("/portal/stundennachweise"),
          api.get<{ items: PortalWartungProject[] }>("/portal/wartungen"),
        ]);
        if (cancelled) return;
        setDocs(docRes.items || []);
        setTimesheets(tsRes.items || []);
        setWartungen(wartRes.items || []);
        setViewerProjectId(null);
        setViewerDetail(null);
      } catch (err) {
        if (cancelled) return;
        setDataError(
          err instanceof ApiError
            ? err.message
            : "Daten konnten nicht geladen werden.",
        );
        setDocs([]);
        setTimesheets([]);
        setWartungen([]);
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [hasCustomerView, user?.effective_email]);

  const filteredDocs = useMemo(() => {
    if (docFilter === "all") return docs;
    return docs.filter((d) => d.dokument_typ === docFilter);
  }, [docs, docFilter]);

  async function onImpersonate(e: FormEvent) {
    e.preventDefault();
    setImpersonateBusy(true);
    setImpersonateError(null);
    try {
      await impersonate(impersonateEmail);
    } catch (err) {
      setImpersonateError(
        err instanceof ApiError ? err.message : "Ansicht konnte nicht gewechselt werden.",
      );
    } finally {
      setImpersonateBusy(false);
    }
  }

  async function onClearImpersonation() {
    setImpersonateBusy(true);
    setImpersonateError(null);
    try {
      await clearImpersonation();
      setImpersonateEmail("");
    } catch (err) {
      setImpersonateError(
        err instanceof ApiError ? err.message : "Zurücksetzen fehlgeschlagen.",
      );
    } finally {
      setImpersonateBusy(false);
    }
  }

  async function openDocPdf(id: number) {
    setPdfError(null);
    try {
      await openAuthenticatedPdf(`/portal/dokumente/${id}/pdf`);
    } catch (err) {
      setPdfError(
        err instanceof ApiError ? err.message : "PDF konnte nicht geöffnet werden.",
      );
    }
  }

  async function openTimesheetPdf(id: number) {
    setPdfError(null);
    try {
      await openAuthenticatedPdf(`/portal/stundennachweise/${id}/pdf`);
    } catch (err) {
      setPdfError(
        err instanceof ApiError ? err.message : "PDF konnte nicht geöffnet werden.",
      );
    }
  }

  async function openWartungViewer(projectId: number) {
    setPdfError(null);
    setWartungDetailLoading(true);
    try {
      const detail = await api.get<PortalWartungDetail>(
        `/portal/wartungen/${projectId}`,
      );
      const normalized: PortalWartungDetail = {
        ...detail,
        sub_locations: Array.isArray(detail.sub_locations)
          ? detail.sub_locations
          : [],
        plaene: Array.isArray(detail.plaene) ? detail.plaene : [],
      };
      setViewerDetail(normalized);
      setViewerProjectId(projectId);
    } catch (err) {
      setViewerDetail(null);
      setViewerProjectId(null);
      setPdfError(
        err instanceof ApiError
          ? err.message
          : "Wartungsdetails konnten nicht geladen werden.",
      );
    } finally {
      setWartungDetailLoading(false);
    }
  }

  function closeWartungViewer() {
    setViewerProjectId(null);
    setViewerDetail(null);
  }

  return (
    <div className="portal-stage relative min-h-screen overflow-hidden">
      <div className="portal-grid pointer-events-none absolute inset-0 opacity-80" />
      <header className="relative z-10 flex items-center justify-between border-b border-black/10 px-6 py-4">
        <div>
          <p className="font-cond text-2xl font-bold uppercase tracking-wide text-ink">
            Ernsthaus
          </p>
          <p className="font-cond text-xs font-semibold uppercase tracking-[0.35em] text-[#9a6c00]">
            Kundenportal
          </p>
        </div>
        <button
          type="button"
          onClick={() => void logout()}
          className="rounded-sm border border-black/15 bg-white/70 px-3 py-1.5 text-sm font-semibold text-ink hover:bg-white"
        >
          Abmelden
        </button>
      </header>

      <main className="relative z-10 mx-auto max-w-4xl px-6 py-10">
        <h1 className="font-cond text-4xl font-bold uppercase tracking-wide text-ink">
          Willkommen, {name}
        </h1>
        <p className="mt-2 text-base text-muted">
          {isStaff
            ? "Mitarbeiter-Ansicht: Wählen Sie eine Kontakt-E-Mail, um das Portal aus Kundensicht zu sehen."
            : "Hier finden Sie Ihre Dokumente, Stundennachweise und Wartungen."}
        </p>

        {isStaff && (
          <section className="mt-8 border border-black/10 bg-white/75 p-5">
            <p className="text-sm font-semibold uppercase tracking-wide text-[#9a6c00]">
              Ansicht als Kunde
            </p>
            {user?.acting_as_email ? (
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-ink">
                  Aktuell:{" "}
                  <strong className="font-semibold">{user.acting_as_email}</strong>
                </p>
                <button
                  type="button"
                  disabled={impersonateBusy}
                  onClick={() => void onClearImpersonation()}
                  className="rounded-sm border border-black/15 bg-white px-3 py-2 text-sm font-semibold text-ink hover:bg-white disabled:opacity-60"
                >
                  Ansicht beenden
                </button>
              </div>
            ) : (
              <form onSubmit={onImpersonate} className="mt-3 flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  required
                  value={impersonateEmail}
                  onChange={(ev) => setImpersonateEmail(ev.target.value)}
                  placeholder="kunde@firma.de"
                  className="min-w-0 flex-1 rounded border border-black/15 bg-white px-3 py-2 text-ink outline-none ring-signal focus:ring-2"
                />
                <button
                  type="submit"
                  disabled={impersonateBusy}
                  className="rounded-sm bg-ink px-4 py-2 text-sm font-bold uppercase tracking-wide text-white hover:bg-black disabled:opacity-60"
                >
                  {impersonateBusy ? "…" : "Anzeigen"}
                </button>
              </form>
            )}
            {impersonateError && (
              <p className="mt-2 text-sm font-medium text-red-600" role="alert">
                {impersonateError}
              </p>
            )}
          </section>
        )}

        {!hasCustomerView && isStaff && (
          <p className="mt-10 text-muted">
            Noch keine Kundenansicht aktiv. Geben Sie oben eine hinterlegte
            Kontakt-E-Mail ein.
          </p>
        )}

        {hasCustomerView && (
          <>
            {user?.acting_as_email && (
              <div className="mt-6 border-l-4 border-[#c48a00] bg-[#c48a00]/10 px-4 py-3 text-sm text-ink">
                Sie sehen das Portal als <strong>{user.acting_as_email}</strong>.
              </div>
            )}

            {pdfError && (
              <p className="mt-4 text-sm font-medium text-red-600" role="alert">
                {pdfError}
              </p>
            )}
            {dataError && (
              <p className="mt-4 text-sm font-medium text-red-600" role="alert">
                {dataError}
              </p>
            )}
            {loadingData && (
              <p className="mt-8 text-muted">Dokumente werden geladen …</p>
            )}

            {!loadingData && (
              <>
                <section className="mt-10">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <h2 className="font-cond text-2xl font-bold uppercase tracking-wide text-ink">
                        Dokumente
                      </h2>
                      <p className="mt-1 text-sm text-muted">
                        Angebote, Bestätigungen, Lieferscheine und Rechnungen
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {FILTERS.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => setDocFilter(f.id)}
                          className={
                            docFilter === f.id
                              ? "rounded-sm bg-ink px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white"
                              : "rounded-sm border border-black/15 bg-white/70 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-ink hover:bg-white"
                          }
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {filteredDocs.length === 0 ? (
                    <p className="mt-4 text-sm text-muted">
                      Keine Dokumente vorhanden.
                    </p>
                  ) : (
                    <ul className="mt-4 divide-y divide-black/10 border border-black/10 bg-white/70">
                      {filteredDocs.map((doc) => (
                        <li
                          key={doc.id}
                          className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0">
                            <p className="font-semibold text-ink">
                              {DOC_TYPE_LABELS[doc.dokument_typ || ""] ||
                                doc.dokument_typ}{" "}
                              {doc.dokument_nummer || `#${doc.id}`}
                            </p>
                            <p className="mt-0.5 truncate text-sm text-muted">
                              {doc.betreff ||
                                doc.auftrag_beschreibung ||
                                doc.kunde_name ||
                                "–"}
                            </p>
                            <p className="mt-1 text-xs text-muted">
                              {formatDate(doc.belegdatum)} ·{" "}
                              {statusLabel(doc.status)} ·{" "}
                              {formatMoney(doc.summe_brutto, doc.waehrung)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => void openDocPdf(doc.id)}
                            className="shrink-0 rounded-sm border border-black/15 bg-white px-3 py-2 text-sm font-semibold text-ink hover:bg-white"
                          >
                            PDF öffnen
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="mt-12">
                  <h2 className="font-cond text-2xl font-bold uppercase tracking-wide text-ink">
                    Stundennachweise
                  </h2>
                  <p className="mt-1 text-sm text-muted">
                    Ihre Nachweise zu den Projekten
                  </p>

                  {timesheets.length === 0 ? (
                    <p className="mt-4 text-sm text-muted">
                      Keine Stundennachweise vorhanden.
                    </p>
                  ) : (
                    <ul className="mt-4 divide-y divide-black/10 border border-black/10 bg-white/70">
                      {timesheets.map((ts) => (
                        <li
                          key={ts.id}
                          className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0">
                            <p className="font-semibold text-ink">
                              Stundennachweis{" "}
                              {ts.display_number ||
                                ts.timesheet_number ||
                                `#${ts.id}`}
                            </p>
                            <p className="mt-0.5 truncate text-sm text-muted">
                              {ts.auftrag_beschreibung ||
                                ts.kunde_name ||
                                ts.auftrag_nummer ||
                                "–"}
                            </p>
                            <p className="mt-1 text-xs text-muted">
                              {formatDate(ts.date_from)}
                              {ts.date_to && ts.date_to !== ts.date_from
                                ? ` – ${formatDate(ts.date_to)}`
                                : ""}{" "}
                              · {statusLabel(ts.status)}
                              {ts.has_signed_pdf ? " · unterschrieben" : ""}
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={!ts.has_pdf}
                            onClick={() => void openTimesheetPdf(ts.id)}
                            className="shrink-0 rounded-sm border border-black/15 bg-white px-3 py-2 text-sm font-semibold text-ink hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {ts.has_pdf ? "PDF öffnen" : "Kein PDF"}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="mt-12 mb-16">
                  <h2 className="font-cond text-2xl font-bold uppercase tracking-wide text-ink">
                    Wartungen
                  </h2>
                  <p className="mt-1 text-sm text-muted">
                    Orte mit Plänen und Ergebnissen – Klick öffnet die Planansicht
                  </p>

                  {wartungDetailLoading && (
                    <p className="mt-3 text-sm text-muted">
                      Wartungsdaten werden geladen …
                    </p>
                  )}

                  {wartungen.length === 0 ? (
                    <p className="mt-4 text-sm text-muted">
                      Keine freigegebenen Wartungen vorhanden.
                    </p>
                  ) : (
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      {wartungen.map((w) => (
                        <button
                          key={w.project_id}
                          type="button"
                          onClick={() => void openWartungViewer(w.project_id)}
                          disabled={wartungDetailLoading}
                          className="border border-black/10 bg-white/70 px-4 py-4 text-left transition hover:border-[#9a6c00]/50 hover:bg-white disabled:opacity-60"
                        >
                          <p className="font-cond text-2xl font-bold uppercase tracking-wide text-ink">
                            {w.ort_name || w.auftragsnummer || `Ort #${w.project_id}`}
                          </p>
                          <p className="mt-1 text-sm text-[#9a6c00]">
                            {w.auftragsnummer || `Projekt #${w.project_id}`}
                          </p>
                          <p className="mt-3 text-xs text-muted">
                            {w.sub_location_count || 0} Unterorte · {w.plan_count || 0}{" "}
                            Pläne · {w.ergebnis_count || 0} Ergebnisse
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}
          </>
        )}
      </main>

      {viewerProjectId != null && viewerDetail && (
        <WartungPlanViewer
          projectId={viewerProjectId}
          detail={viewerDetail}
          onClose={closeWartungViewer}
        />
      )}
    </div>
  );
}
